import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';
import { Skeleton } from '../../ui/skeleton';
import { StageBadge } from '../QualityBadges';
import { formatDate, scoreTone, timeAgo } from '../qualityConfig';

const Mini = ({ label, value, tone = '' }) => (
    <div className="rounded-xl border bg-muted/20 p-3 text-center">
        <p className={`font-heading text-2xl font-bold tabular-nums ${tone}`}>{value ?? '—'}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    </div>
);

export const UserHistorySheet = ({ api, member, refreshKey, onOpenChange, onViewTask }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        if (!member) return;
        setData(null);
        api.get('/quality-tasks/admin/tasks', { params: { user_id: member.id } }).then(({ data: d }) => setData(d)).catch(() => setData({ tasks: [], summary: null }));
    }, [api, member, refreshKey]);
    const s = data?.summary;

    return (
        <Sheet open={Boolean(member)} onOpenChange={onOpenChange}>
            <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="user-history-sheet">
                <SheetHeader className="text-left">
                    <SheetTitle className="font-heading text-2xl tracking-tight">{member?.name}</SheetTitle>
                    <SheetDescription>Quality task history {member?.department ? `· ${member.department}` : ''}</SheetDescription>
                </SheetHeader>
                {!data ? <Skeleton className="mt-6 h-40 w-full" /> : (
                    <div className="mt-6 space-y-5">
                        {s && (
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="user-history-summary">
                                <Mini label="Not opened" value={s.not_opened} tone={s.not_opened ? 'text-rose-600' : ''} /><Mini label="In progress" value={s.in_progress} tone={s.in_progress ? 'text-sky-600' : ''} />
                                <Mini label="Done" value={s.completed} /><Mini label="Missed" value={s.missed} tone={s.missed ? 'text-rose-600' : ''} />
                                <Mini label="Avg" value={s.avg_score} tone={scoreTone(s.avg_score)} />
                            </div>
                        )}
                        <ul className="space-y-2">
                            {data.tasks.map((t) => (
                                <li key={t.id}>
                                    <button type="button" onClick={() => onViewTask(t)} className="flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors hover:bg-muted/40" data-testid={`user-history-task-${t.id}`}>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-medium">{t.title}</p>
                                            <p className="text-xs text-muted-foreground">Assigned {formatDate(t.created_at)} · due {formatDate(t.deadline)} · {t.completed_count}/{t.total_count} done</p>
                                            <p className={`text-xs ${t.opened ? 'text-indigo-600 dark:text-indigo-300' : 'font-medium text-rose-600 dark:text-rose-400'}`}>
                                                {t.opened ? `Seen ${timeAgo(t.seen_at || t.started_at || t.updated_at)}` : 'Not opened yet'}
                                            </p>
                                            <div className="mt-1.5"><StageBadge stage={t.stage} dueSoon={t.due_soon} /></div>
                                        </div>
                                        <p className={`font-heading text-xl font-bold tabular-nums ${scoreTone(t.score ?? t.score_so_far)}`}>{t.score ?? t.score_so_far ?? '—'}</p>
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                    </button>
                                </li>
                            ))}
                            {data.tasks.length === 0 && <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No tasks assigned yet.</p>}
                        </ul>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};
